/**
 * What a FlashBook post says about the book it holds.
 *
 * A published book is two records: the post (which the feed carries) and the
 * `app.bsky.feed.flashbook` record it points at (which the feed does not). So a
 * card for the post has to be built from two sources of different speed, and the
 * interesting part of this module is that *both* are optional:
 *
 *  - the **post record**, available on the first paint, which carries the book's
 *    URI, its page count, how much of it is work, and which *kind* of cover it has
 *    along with that cover's picture (see the publisher);
 *  - the **book record**, which needs a request, and is where the cover art, the
 *    film a cover plays, the theme and the authoritative counts live.
 *
 * Neither source alone is enough to draw a card, and a card that waits for the
 * slower one is a blank row in a feed. So the facts are merged here, once, with
 * the rule that the newer record wins where the two disagree — and the numbers
 * come back as *numbers* and *nullable* fields, never as pre-formatted strings,
 * because what the card should say about a book with zero activities is a
 * rendering decision and this module has no renderer.
 *
 * The one structural judgement here is `isFlashbookPost`: a flashbook post is
 * recognised by its own record's marker *or* by the back-ref the publisher
 * patches in afterwards, because the patch is a second write and a post whose
 * second write failed is still a book. Getting that wrong is not a cosmetic bug:
 * it is a published book that shows in the feed as a bare line of text.
 */
import {SLIDE_TYPE_META} from '#/types/capsule'

/** The back-ref the publisher patches onto the post, as the feed hands it over. */
export type FlashBookRef = {uri: string; cid?: string}

/** The book record, as the query that fetches it resolves it. */
export type FlashBookCardRecord = {
  uri: string
  title?: string
  description?: string
  themeId?: string
  status?: 'draft' | 'published'
  cover?: {
    kind: 'image' | 'audio' | 'video'
    imageUri?: string
    audioUri?: string
    videoUri?: string
    videoThumbnailUri?: string
    /** Which minute the cover film opens on (see `#/lib/coverTrim`). */
    videoStartMs?: number
  }
  pages?: {id: string; blocks?: unknown[]}[]
  pageCount?: number
  exerciseCount?: number
  subject?: string
  level?: string
}

export type FlashBookCoverKind = 'image' | 'audio' | 'video' | 'none'

export type FlashBookCardFacts = {
  /** The book record this post points at, if the post says. */
  bookUri: string | null
  /** What to call the book: its own title, else the post's first line. */
  title: string
  /** The theme the book was written in, as the registry names it. */
  themeId: string
  /** How many pages, or null when nothing has said — never a stand-in zero. */
  pageCount: number | null
  /** How many blocks are exercises, or null when nothing has said. */
  activityCount: number | null
  /** The stored codes, `Level_B1` and `Maths`, or null when the book names none. */
  level: string | null
  subject: string | null
  coverKind: FlashBookCoverKind
  /** The still to draw: the cover art, or a film or audio cover's own frame. */
  coverImageUri: string | null
  /**
   * The cover film itself, when the cover is one.
   *
   * Handed over because a book published before the record carried a still has
   * none, and the only picture of that film is the film — so the card is given
   * what it needs to read the frame rather than being left to hunt for it.
   */
  coverFilmUri: string | null
  /** Which minute the cover film opens on, in ms. */
  coverStartMs: number
  /** Whether the cover carries a spoken introduction. */
  hasIntroAudio: boolean
  /** Whether the book's cover is a promotional film rather than a still. */
  hasCoverFilm: boolean
}

/** A post, as loosely as the feed hands it over — extra fields and all. */
type Postish = {
  record?: unknown
  flashbook?: unknown
}

/**
 * The DID whose repo holds the book a post points at — its author.
 *
 * Asked of the *book's own* URI rather than of the post's author, because those are
 * two answers to one question and only one of them is about the book. The record
 * lives in a repo, and the account that holds it is the person who set the book —
 * which is the same rule the reader uses to decide whose class a book has (see
 * `#/screens/FlashBookPlay`), so a card and the book cannot disagree about who the
 * author is. A post that named somebody else's record (nothing stops it) would
 * otherwise offer *your* class report on *their* book.
 *
 * Only a `did:` authority counts. A repo is addressed by DID, and the account being
 * compared against is a DID, so a handle here could never match — returning it would
 * only turn a shape that cannot happen into a comparison that silently fails.
 */
export function flashbookCreatorOf(
  bookUri: string | null | undefined,
): string | null {
  if (typeof bookUri !== 'string' || !bookUri.startsWith('at://')) return null
  const authority = bookUri.slice('at://'.length).split('/')[0] ?? ''
  return authority.startsWith('did:') ? authority : null
}

/** Read the book back-ref off a post, from any of the places it may sit. */
export function flashbookRefOf(post: Postish | undefined): FlashBookRef | null {
  if (!post) return null
  const record = (post.record ?? {}) as Record<string, unknown>
  for (const candidate of [post.flashbook, record.flashbook]) {
    if (
      candidate &&
      typeof candidate === 'object' &&
      typeof (candidate as {uri?: unknown}).uri === 'string'
    ) {
      const ref = candidate as {uri: string; cid?: unknown}
      return {
        uri: ref.uri,
        cid: typeof ref.cid === 'string' ? ref.cid : undefined,
      }
    }
  }
  return null
}

/**
 * Whether this post is a book.
 *
 * True for a post the publisher marked (`type: 'flashbook'`) and for one carrying
 * the back-ref — and, deliberately, for a post that has *both* missing but was
 * written by a build of the studio that predates either. That last case is what
 * the caller's reverse lookup is for, so this predicate is about the evidence on
 * the post itself and nothing else: it never guesses.
 */
export function isFlashbookPost(post: Postish | undefined): boolean {
  if (!post) return false
  const record = (post.record ?? {}) as Record<string, unknown>
  return record.type === 'flashbook' || !!flashbookRefOf(post)
}

/** The first line of a post's own text — the title the creator typed. */
function firstLineOf(text: unknown): string {
  if (typeof text !== 'string') return ''
  const line = text.split('\n').find(part => part.trim().length > 0)
  return line?.trim() ?? ''
}

/** A count that is a count: whole, not negative, and not a string. */
function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null
}

/**
 * How many blocks in a book are exercises, when the book did not say.
 *
 * The fallback for a book published before the count was stored, or one whose
 * record was written by hand: it reads the vocabulary rather than restating it,
 * because a second copy of "which blocks are work" is a second answer to the
 * question, and the one that drifts is always the copy nobody looks at.
 */
function countExercisesInPages(pages: FlashBookCardRecord['pages']): number {
  if (!Array.isArray(pages)) return 0
  let total = 0
  for (const page of pages) {
    const blocks = Array.isArray(page?.blocks) ? page.blocks : []
    for (const block of blocks) {
      const type = (block as {type?: unknown} | null)?.type
      if (
        typeof type === 'string' &&
        SLIDE_TYPE_META[type as keyof typeof SLIDE_TYPE_META]?.isExercise
      ) {
        total++
      }
    }
  }
  return total
}

/**
 * The facts a post and its book record agree on.
 *
 * `book` is the record the caller fetched, when it has one. Everything on it is
 * optional, because a book written before a field existed is still a book.
 */
export function flashbookCardFacts({
  post,
  book,
}: {
  post: Postish | undefined
  book?: FlashBookCardRecord | null
}): FlashBookCardFacts {
  const record = (post?.record ?? {}) as Record<string, unknown>
  const ref = flashbookRefOf(post)

  const pages = book?.pages
  /*
   * Null means *unknown*, and it is deliberately not the same as zero.
   *
   * The card is drawn twice: once from the post alone, while the record is still
   * in flight, and once from both. A book whose record never arrives has an
   * unknown length, and "0 pages" would be a statement about the book rather than
   * about the request — the one reading that is simply false. So an unknown count
   * travels as null and the card leaves the line out.
   */
  const pageCount =
    count(book?.pageCount) ??
    (Array.isArray(pages) ? pages.length : null) ??
    count(record.pageCount) ??
    null
  const activityCount =
    count(book?.exerciseCount) ??
    count(record.exerciseCount) ??
    (Array.isArray(pages) ? countExercisesInPages(pages) : null)

  const cover = book?.cover
  /*
   * Which cover this is, from whichever source has arrived.
   *
   * The record is authoritative — it is the same data the reader plays — and the
   * post's own marker is what stands in for it on the first paint, so that a book
   * whose cover is a film says so before the record lands rather than announcing
   * itself as a picture and then changing its mind.
   */
  const postCoverKind = record.coverKind
  const coverKind: FlashBookCoverKind =
    cover?.kind ??
    (typeof postCoverKind === 'string' &&
    (['image', 'audio', 'video'] as const).includes(
      postCoverKind as 'image' | 'audio' | 'video',
    )
      ? (postCoverKind as FlashBookCoverKind)
      : 'none')

  /*
   * The still, in the order the card can get one.
   *
   * The **post's own field first**, because it needs no request: publishing writes
   * the cover's picture onto the post (see the publisher) precisely so that the
   * first paint of a feed row has a cover on it. Then the record's own artwork, or
   * the still derived from a film when the record has one — and the record wins over
   * the post wherever the two disagree, since it is what the reader plays.
   *
   * The order used to be the artwork alone, which meant a film cover had nothing to
   * draw at all and every card showed a flat rectangle of theme colour.
   */
  const coverImageUri =
    cover?.imageUri ??
    cover?.videoThumbnailUri ??
    (typeof record.coverImageUri === 'string' ? record.coverImageUri : null)

  const themeId =
    (typeof book?.themeId === 'string' && book.themeId) ||
    (typeof record.themeId === 'string' && record.themeId) ||
    'storybook'

  return {
    bookUri: book?.uri ?? ref?.uri ?? null,
    title:
      (book?.title && book.title.trim()) ||
      firstLineOf(record.text) ||
      'FlashBook',
    themeId,
    pageCount,
    activityCount,
    level: book?.level ?? null,
    subject: book?.subject ?? null,
    coverKind,
    coverImageUri,
    coverFilmUri:
      coverKind === 'video' && typeof cover?.videoUri === 'string'
        ? cover.videoUri
        : null,
    coverStartMs:
      typeof cover?.videoStartMs === 'number' && cover.videoStartMs > 0
        ? Math.round(cover.videoStartMs)
        : 0,
    hasIntroAudio: coverKind === 'audio' || !!cover?.audioUri,
    hasCoverFilm: coverKind === 'video',
  }
}
